import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { IProps } from '../common/props';
import { Button } from '../button/Button';
import {
  getFileNameSplit,
  iconByMimeType,
  mimeTypeByFileName,
} from '../icon/mimeUtils';
import { ProgressBar } from '../progress-bar/ProgressBar';
import { Icon } from '../icon/Icon';
import {
  FileInputValue,
  UloadingInfo,
  UPLOAD_PROGRESS_INFINITE,
} from './props';
import { mcClear } from '@mc/react-ui-icons';

export interface IFileInputFileProps<VALUE extends FileInputValue>
  extends IProps {
  onDelete: () => void;
  disabled?: boolean;
  uploadingInfo?: UloadingInfo;
  hideDelete?: boolean;
  /**
   * Načtení href pro stažení souboru pro předanou hodnotu, toto se využije pouze pokud není href definován přímo v hodnotě.
   */
  getHref?: (value: VALUE) => string;
  value?: VALUE;
  uploadingProgress: number;
  /**
   * Pokud není uveden href ve value a není definována metoda getHref, tak se při kliknutí na soubor zavolá tato metoda.
   */
  onDownload?: (value: FileInputValue) => void;
}

export type FileInputFileProps<VALUE extends FileInputValue> =
  IFileInputFileProps<VALUE>;

export const FileInputFile: <VALUE extends FileInputValue>(
  props: FileInputFileProps<VALUE>
) => React.ReactElement<FileInputFileProps<VALUE>> = ({
  className,
  onDelete,
  uploadingInfo: uploadingInfo,
  disabled,
  value,
  uploadingProgress,
  getHref,
  onDownload,
  hideDelete,
}) => {
  let mimeType;
  let fileName;
  if (uploadingInfo) {
    mimeType = uploadingInfo.type || mimeTypeByFileName(uploadingInfo.fileName);
    fileName = uploadingInfo.fileName;
  } else {
    mimeType = value?.type ? value.type : mimeTypeByFileName(value?.name);
    fileName = value?.name;
  }

  let href;
  if (value) {
    href = value.href || (getHref && getHref(value));
  }

  const linkDisabled = !!uploadingInfo;
  const fileNameSplit = getFileNameSplit(fileName);

  return (
    <div
      className={classNames(
        className,
        Classes.FILE_INPUT,
        Classes.FILE_INPUT_UPLOADING
      )}
    >
      <Button
        className={Classes.FILE_INPUT_FILE_BUTTON}
        icon={<Icon icon={iconByMimeType(mimeType)} />}
        style="link"
        link={
          !linkDisabled
            ? href
              ? { to: { pathname: href }, target: '_blank' }
              : undefined
            : undefined
        }
        onClick={
          !linkDisabled
            ? href
              ? undefined
              : () => onDownload && value && onDownload(value)
            : undefined
        }
        disabled={linkDisabled}
      >
        <div className={classNames(Classes.FILE_INPUT_FILENAME_WRAPPER)}>
          <span className={Classes.FILE_INPUT_FILENAME}>
            {fileNameSplit[0]}
          </span>
          <span className={Classes.FILE_INPUT_EXTENSION}>
            {fileNameSplit[1]}
          </span>
          {uploadingInfo && (
            <div className={Classes.FILE_INPUT_FILENAME_UPLOADING}>
              <ProgressBar
                small
                intent={'primary'}
                value={uploadingProgress}
                infinite={uploadingProgress === UPLOAD_PROGRESS_INFINITE}
              />
            </div>
          )}
        </div>
      </Button>
      {!hideDelete && (
        <Button
          onClick={onDelete}
          disabled={disabled}
          className={Classes.FILE_INPUT_ACTION}
          icon={<Icon icon={mcClear} />}
        />
      )}
    </div>
  );
};
